<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 选择功能（仅创建时） -->
    <div v-if="!isEdit">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        选择功能 <span class="text-red-500">*</span>
      </label>
      <select v-model="formData.featureId" required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
        <option value="" disabled>请选择功能</option>
        <option v-for="feature in availableFeatures" :key="feature.id" :value="feature.id">
          {{ displayLocalizedText(feature.name) }} ({{ feature.featureCode }})
        </option>
      </select>
    </div>

    <!-- 功能信息（编辑时显示） -->
    <div v-else class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <div class="text-sm text-gray-600 dark:text-gray-400">功能名称</div>
      <div class="text-base font-medium text-gray-900 dark:text-gray-100">{{ displayLocalizedText(planFeature?.feature.name) }}</div>
      <div class="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1">{{ planFeature?.feature.featureCode }}</div>
    </div>

    <!-- 配额类型 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        配额类型 <span class="text-red-500">*</span>
      </label>
      <select v-model="formData.quotaType" required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        @change="handleQuotaTypeChange">
        <option value="" disabled>请选择配额类型</option>
        <option v-for="option in quotaTypeOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- 配额限制（根据配额类型显示不同的输入） -->
    <div v-if="formData.quotaType && formData.quotaType !== QuotaType.UNLIMITED">
      <!-- 布尔型配额 -->
      <div v-if="formData.quotaType === QuotaType.BOOLEAN">
        <label class="flex items-center">
          <input v-model="booleanValue" type="checkbox"
            class="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded" />
          <span class="text-sm text-gray-700 dark:text-gray-300">启用该功能</span>
        </label>
      </div>

      <!-- 数值型配额 -->
      <div v-else class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">软限制（提示）</label>
          <input v-model.number="formData.softLimit" type="number" min="0"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
            placeholder="例如: 40" />
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">达到此值时提示用户</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            硬限制（禁止） <span class="text-red-500">*</span>
          </label>
          <input v-model.number="formData.hardLimit" type="number" min="0" required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
            placeholder="例如: 50" />
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">达到此值时禁止使用</p>
        </div>
      </div>
    </div>

    <!-- 重置周期 -->
    <div v-if="showResetPeriod">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">重置周期</label>
      <select v-model="formData.resetPeriod"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
        <option value="">不重置</option>
        <option value="DAILY">每日重置</option>
        <option value="MONTHLY">每月重置</option>
        <option value="YEARLY">每年重置</option>
      </select>
    </div>

    <!-- 按钮 -->
    <div class="flex justify-end gap-3 pt-4">
      <button type="button" @click="handleCancel"
        class="cancel-btn">
        取消
      </button>
      <button type="submit" class="primary-btn">
        {{ isEdit ? '更新' : '添加' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { getQuotaTypeOptions, QuotaType } from '@/types/membership/enums'
import type {
  Feature,
  PlanFeature,
  CreatePlanFeatureRequest,
  UpdatePlanFeatureRequest,
} from '@/types/membership/feature'
import { displayLocalizedText } from '@/types/membership/localized'

/**
 * Props
 */
interface Props {
  planFeature?: PlanFeature
  availableFeatures?: Feature[]
  isEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
  availableFeatures: () => [],
})

/**
 * Emits
 */
const emit = defineEmits<{
  submit: [data: CreatePlanFeatureRequest | UpdatePlanFeatureRequest]
  cancel: []
}>()

/**
 * 表单数据
 */
const formData = ref<CreatePlanFeatureRequest>({
  featureId: 0,
  quotaType: '' as QuotaType,
  softLimit: undefined,
  hardLimit: undefined,
  resetPeriod: undefined,
})

/**
 * 布尔值（用于布尔型配额）
 */
const booleanValue = ref(false)

/**
 * 配额类型选项
 */
const quotaTypeOptions = getQuotaTypeOptions()

/**
 * 是否显示重置周期
 */
const showResetPeriod = computed(() => {
  return (
    formData.value.quotaType === QuotaType.DAILY ||
    formData.value.quotaType === QuotaType.MONTHLY ||
    formData.value.quotaType === QuotaType.YEARLY
  )
})

/**
 * 监听 planFeature prop 变化，初始化表单
 */
watch(
  () => props.planFeature,
  (newPlanFeature) => {
    if (newPlanFeature) {
      formData.value = {
        featureId: newPlanFeature.featureId,
        quotaType: newPlanFeature.quotaType,
        softLimit: newPlanFeature.softLimit,
        hardLimit: newPlanFeature.hardLimit,
        resetPeriod: newPlanFeature.resetPeriod,
      }

      // 如果是布尔型，设置布尔值
      if (newPlanFeature.quotaType === QuotaType.BOOLEAN) {
        booleanValue.value = newPlanFeature.hardLimit === 1
      }
    }
  },
  { immediate: true }
)

/**
 * 监听布尔值变化，更新 hardLimit
 */
watch(booleanValue, (newValue) => {
  if (formData.value.quotaType === QuotaType.BOOLEAN) {
    formData.value.hardLimit = newValue ? 1 : 0
    formData.value.softLimit = undefined
  }
})

/**
 * 处理配额类型变化
 */
const handleQuotaTypeChange = () => {
  // 重置配额限制
  if (formData.value.quotaType === QuotaType.BOOLEAN) {
    formData.value.hardLimit = booleanValue.value ? 1 : 0
    formData.value.softLimit = undefined
    formData.value.resetPeriod = undefined
  } else if (formData.value.quotaType === QuotaType.UNLIMITED) {
    formData.value.hardLimit = undefined
    formData.value.softLimit = undefined
    formData.value.resetPeriod = undefined
  } else {
    formData.value.hardLimit = undefined
    formData.value.softLimit = undefined
    formData.value.resetPeriod = formData.value.quotaType.toUpperCase()
  }
}

/**
 * 处理提交
 */
const handleSubmit = () => {
  const normalizeOptionalNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return null
    }
    if (Number.isNaN(value)) {
      return null
    }
    return value
  }

  const normalizeOptionalString = (value: string | null | undefined) => {
    if (value === '' || value === null || value === undefined) {
      return null
    }
    return value
  }

  emit('submit', {
    ...formData.value,
    softLimit: normalizeOptionalNumber(formData.value.softLimit),
    hardLimit: normalizeOptionalNumber(formData.value.hardLimit),
    resetPeriod: normalizeOptionalString(formData.value.resetPeriod),
  })
}

/**
 * 处理取消
 */
const handleCancel = () => {
  emit('cancel')
}
</script>
